import TimelineTrack, {TimelineTrackSaveObject} from './TimelineTrack';
import {TimelineFragment, TimelineFragmentExportData} from "./TimelineFragment";
import TimelineEditorUtilities from '../../components/timeline/TimelineEditorUtilities';
import {assert} from '../utilities/ErrorHandling';
import store, {StoreActions} from '../../state/store';
import {ITimeline, TimelineElementTypes} from "../../interfaces/Timeline";
import {CustomEventTypes, TimelineTrackTypes} from "../../globals";
import Engine from "../Engine";
import Scenario from '../scenarioElements/Scenario';
import { getUuidv4 } from '../utilities/Misc';
import {RegisterClassWithAPI} from "../api/Decorators";
import {CreateTimeline, CreateTimelineFromSaveData, timelineElementMap, timelineStepMap} from '.';

const timelineDataObject = {
	timelineId: "",
	stepId: "",
	sceneId: "",
	fps: 30,
	loopTime: 10,
	totalTime: 11,
	isLoopEnabled: true,
	currentTime: 0,
	isRecording: false,
	isPlaying: false,
};

export interface TimelineSaveData
{
	groups: any;
	id: string;
	stepId: string;
	sceneId: string;
	loopTime: number;
	totalTime: number;
	isLoopEnabled: boolean;
	fps: number;
	tracks: TimelineTrackSaveObject[];
}

export interface TimelinesSaveData
{
	timelines: {
		[key: string]: TimelineSaveData;
	};
}

export interface TimelineExportData
{
	stepId: string;
	loopTime: number;
	totalTime: number;
	looping: boolean;
	fragments: TimelineFragmentExportData[];
}

@RegisterClassWithAPI("Timeline", {}, "An instance of a timeline. Do not instantiate the timeline directly, get it from step.timeline")
export class Timeline implements ITimeline
{
	#id: string;
	#associatedStepId: string;
	#associatedSceneId: string;
	#updated: boolean;
	private savedData: TimelineSaveData | null = null;

	loaded: boolean = false;
	fragments: TimelineFragment[];
	actionsTrack: TimelineTrack | null;
	private endOfFrameEvents: any[];
	objectsTrack: TimelineTrack | null;

	constructor(stepId: string, sceneId: string, loopTime: number)
	{
		this.#id = getUuidv4();
		this.#associatedStepId = stepId;
		this.#associatedSceneId = sceneId;
		this.#updated = false;

		const dataObject = {
			...timelineDataObject,
			timelineId: this.id,
			stepId: this.associatedStepId,
			sceneId: this.associatedSceneId,
		};

		if (loopTime !== undefined)
		{
			dataObject.loopTime = loopTime;
			dataObject.totalTime = loopTime + 1;
		}

		store.dispatch(StoreActions.timeline.setElementData({ dataId: this.id, data: dataObject }));

		this.actionsTrack = new TimelineTrack("Actions Track", this, TimelineTrackTypes.ActionsTrack);
		this.objectsTrack = new TimelineTrack("New Track", this, TimelineTrackTypes.ObjectsTrack);
		this.fragments = [];
		this.endOfFrameEvents = [];

		timelineStepMap.set(stepId, this);
		timelineElementMap.set(this.id, this);
	}

	set saveData(saveData: TimelineSaveData)
	{
		this.savedData = { ...saveData };
	}

	getDataObject()
	{
		return store.getState().timelineModule.elementData[this.id];
	}

	get associatedStepId()
	{
		return this.#associatedStepId;
	}

	get associatedSceneId()
	{
		return this.#associatedSceneId;
	}

	get id()
	{
		return this.#id;
	}

	get fps()
	{
		return this.getDataObject().fps;
	}

	get updated()
	{
		return this.#updated;
	}

	set fps(fps: number)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "fps", propertyValue: fps }));
	}

	get loopTime()
	{
		return this.getDataObject().loopTime;
	}

	set loopTime(loopTime: number)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "loopTime", propertyValue: loopTime }));
	}

	get totalTime()
	{
		return this.getDataObject().totalTime;
	}

	set totalTime(totalTime: number)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "totalTime", propertyValue: totalTime }));
	}

	get isLoopEnabled()
	{
		return this.getDataObject().isLoopEnabled;
	}

	set isLoopEnabled(enabled: boolean)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "isLoopEnabled", propertyValue: enabled }));
	}

	get currentTime()
	{
		return this.getDataObject().currentTime;
	}

	set currentTime(time: number)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "currentTime", propertyValue: time }));
	}

	static exportTimelines()
	{
		const output = { timelines: {} };

		for (const step of Scenario.getSteps())
		{
			if (!step.timeline) continue;
			output.timelines[step.timeline.id] = step.timeline.export();
		}

		return output;
	}

	export(): TimelineExportData
	{
		this.load();

		const output: TimelineExportData = {
			stepId: this.#associatedStepId,
			loopTime: this.loopTime,
			totalTime: this.totalTime,
			looping: this.isLoopEnabled,
			fragments: [] as TimelineFragmentExportData[],
		};

		this.fragments.forEach((fragment) =>
		{
			if (fragment.channel.object.getSceneObjects().length) output["fragments"].push(fragment.export());
		});

		return output;
	}

	resetAnimations()
	{
		if (this.#updated)
		{
			this.load();

			const reverseOrderFragments = this.fragments.slice().reverse();
			reverseOrderFragments.forEach((fragment) => fragment.resetAnimations());
			this.#updated = false;
		}
	}

	update(time = this.currentTime)
	{
		this.load();

		if (time === this.currentTime && this.#updated) return;

		Engine.getInstance()?.getGizmoManager()?.exitFreeMoveMode();
		this.resetAnimations();
		this.fragments.forEach((fragment) => fragment.update(time));
		this.currentTime = time;

		for (const endFrameEvent of this.endOfFrameEvents)
		{
			document.dispatchEvent(new CustomEvent(endFrameEvent, { detail: { sender: this } }));
		}
		this.endOfFrameEvents.length = 0;
		this.#updated = true;
	}

	pushEndOfFrameEvent(event: CustomEventTypes)
	{
		if (!this.endOfFrameEvents.includes(event))
		{
			this.endOfFrameEvents.push(event);
		}
	}

	reverse()
	{
		this.load();

		if (this.#updated)
		{
			this.resetAnimations();
			this.fragments.forEach((fragment) =>
			{
				fragment.reverse(true);
			});

			this.sortFragments();
			this.update();
			store.dispatch(StoreActions.timeline.updateEditorUI());
		}
		else
		{
			this.fragments.forEach((fragment) =>
			{
				fragment.reverse(true);
			});
		}
		this.sortFragments();

		// WC20-2330: offset fragment times to eliminate empty space from the start
		// WC20-3010: remove "time gap" whether or not the timeline is visible in the editor
		// time offset = start time of earliest fragment
		const timeOffset = this.fragments[0]?.startTime ? this.fragments[0]?.startTime : 0; //fixed a crash of trying to reverse a step with empty timeline
		this.fragments.forEach((fragment) =>
		{
			fragment.startTime -= timeOffset;
			fragment.endTime -= timeOffset;
		});
	}

	save(shouldIncludeChildren: boolean)
	{
		const output: TimelineSaveData = {
			id: this.#id,
			stepId: this.#associatedStepId,
			sceneId: this.#associatedSceneId,
			loopTime: this.loopTime,
			totalTime: this.totalTime,
			isLoopEnabled: this.isLoopEnabled,
			fps: this.fps,
			tracks: [] as TimelineTrackSaveObject[],
			groups: null // TODO maybe not used?
		};

		if (shouldIncludeChildren)
		{
			output.tracks = [];
			if (this.actionsTrack)
			{
				output.tracks.push(this.actionsTrack.save(shouldIncludeChildren));
			}
			if (this.objectsTrack)
			{
				output.tracks.push(this.objectsTrack.save(shouldIncludeChildren));
			}
		}

		return output;
	}

	load()
	{
		if (this.loaded) return;
		this.loaded = true;

		if (!this.savedData) return;

		this.clear();

		this.#id = this.savedData.id;
		this.#associatedStepId = this.savedData.stepId;
		if (this.savedData.sceneId)
		{
			this.#associatedSceneId = this.savedData.sceneId;
		}
		else
		{
			this.#associatedSceneId = store.getState().scenarioModule.elementData[this.#associatedStepId].sceneId;
		}

		assert(this.#associatedSceneId);

		this.fps = this.savedData.fps;
		this.loopTime = this.savedData.loopTime;
		this.totalTime = this.savedData.totalTime;
		this.isLoopEnabled = this.savedData.isLoopEnabled;
		this.currentTime = 0;
		timelineElementMap.set(this.id, this);

		this.actionsTrack = new TimelineTrack("Actions Track", this, TimelineTrackTypes.ActionsTrack);
		this.objectsTrack = new TimelineTrack("New Track", this, TimelineTrackTypes.ObjectsTrack);

		if (this.savedData.groups) // TODO Is this used anymore?
		{
			this.objectsTrack.load(this.savedData.groups[0]);
		}
		else if (this.savedData.tracks)
		{
			this.actionsTrack.load(this.savedData.tracks[0]);
			this.objectsTrack.load(this.savedData.tracks[1]);
		}

		// Clear as we don't need this anymore
		this.savedData = null;

		this.onAdded();
	}

	clone(newStepId: string, sceneId: string)
	{
		this.load();

		assert(newStepId && !timelineStepMap.has(newStepId)); //timelines will never be owned by the same step. a clone must be given a new step id
		assert(sceneId);

		// make a new timeline object
		const newTimeline = CreateTimeline(newStepId, sceneId);

		console.assert(newTimeline);
		if (!newTimeline)
		{
			return;
		}

		// copy parameters
		newTimeline.fps = this.fps;
		newTimeline.loopTime = this.loopTime;
		newTimeline.totalTime = this.totalTime;
		newTimeline.isLoopEnabled = this.isLoopEnabled;

		newTimeline.actionsTrack?.deleteSelf();
		if (this.actionsTrack)
		{
			newTimeline.actionsTrack = this.actionsTrack.clone(newTimeline, true);
		}
		newTimeline.objectsTrack?.deleteSelf();
		if (this.objectsTrack)
		{
			newTimeline.objectsTrack = this.objectsTrack.clone(newTimeline, true);
		}
		newTimeline.onAdded();

		return newTimeline;
	}

	addFragment(fragment: TimelineFragment)
	{
		if (this.fragments.indexOf(fragment) < 0)
		{
			this.fragments.push(fragment);
			this.sortFragments();
		}
	}

	removeFragment(fragment: TimelineFragment)
	{
		const index = this.fragments.indexOf(fragment);
		if (index > -1)
		{
			this.fragments.splice(index, 1);
		}
	}

	sortFragments()
	{
		if (this.#updated)
		{
			this.resetAnimations();
			this.fragments.sort(TimelineEditorUtilities.sortByStartTime);
			this.update();
		}
		else
		{
			this.fragments.sort(TimelineEditorUtilities.sortByStartTime);
		}
	}

	getType()
	{
		return TimelineElementTypes.Timeline;
	}

	getChildren()
	{
		this.load();
		return [this.actionsTrack, this.objectsTrack];
	}

	clear()
	{
		this.resetAnimations();
		this.actionsTrack?.deleteSelf();
		this.objectsTrack?.deleteSelf();
		this.fragments.length = 0;
	}

	deleteSelf()
	{
		this.clear();

		timelineElementMap.delete(this.id);
	}

	onDelete()
	{
		this.getChildren().forEach((child) => { if (child) child.onDelete() });
	}

	onAdded()
	{
		this.getChildren().forEach((child) => { if (child) child.onAdded() });
	}

	getEndTime()
	{
		let t = 0;
		this.fragments.forEach((fragment) =>
		{
			t = Math.max(t, fragment.endTime);
		});

		return t;
	}

	reset()
	{
		this.clear();

		this.fps = 30;
		this.loopTime = 10;
		this.totalTime = 11;
		this.isLoopEnabled = true;
	}

	static clearTimelinesData()
	{
		for (const timeline of timelineStepMap.values())
		{
			timeline.resetAnimations();
			for (const fragment of timeline.fragments)
			{
				fragment.channel.object.sceneObjectGuid = undefined;
				fragment.channel.object.objectGroupGuid = undefined;
				fragment.channel.object.associatedObject = undefined;
			}
		}
		for (const stepId of timelineStepMap.keys())
		{
			if (timelineStepMap.has(stepId))
			{
				timelineStepMap.get(stepId)?.deleteSelf();
				timelineStepMap.delete(stepId);
			}
		}

		store.dispatch(StoreActions.timeline.updateEditorState({ timelineId: undefined }));
	}

	static saveTimelines()
	{
		const output: TimelinesSaveData = { timelines: {} };

		for (const step of Scenario.getSteps())
		{
			if (!step.timeline) continue;
			output.timelines[step.timeline.id] = step.timeline.savedData || step.timeline.save(true);
		}

		return output;
	}

	static loadTimelines(saveData: TimelinesSaveData)
	{
		Timeline.clearTimelinesData();

		for (const timelineSaveData of Object.values(saveData.timelines))
		{
			CreateTimelineFromSaveData(timelineSaveData);
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.timelineDataLoaded));
	}
}
